import React from "react";
import { ResizeHandleButton } from "./splitViewResize";

export const ALERTS_TABLE_COLUMN_BASE_WIDTH = 650;
export const ALERTS_TABLE_INNER_BASE_WIDTH = 655;
export const ALERTS_TABLE_MAX_OFFSET = 400;
export const ALERTS_TABLE_MIN_OFFSET = -150;
export const ALERTS_TABLE_SEEN_COLUMN_WIDTH = 20;

export const alertsTableSeenColumnSx = {
  width: ALERTS_TABLE_SEEN_COLUMN_WIDTH,
  maxWidth: ALERTS_TABLE_SEEN_COLUMN_WIDTH,
  minWidth: ALERTS_TABLE_SEEN_COLUMN_WIDTH,
  padding: "4px 0 4px 4px",
  boxSizing: "border-box" as const,
  overflow: "hidden",
};

/** Site / camera name cells: wrap within fixed table columns instead of overlapping neighbors. */
export const alertsTableSiteCameraCellSx = {
  fontSize: "11.5px",
  fontFamily: "Mulish",
  whiteSpace: "normal" as const,
  wordBreak: "break-word" as const,
  overflowWrap: "anywhere" as const,
  verticalAlign: "top" as const,
};

// Default (pre-drag) width grows with the viewport so wide monitors get a
// comfortable list (ENG-1464), but never drops below the fixed base, so
// laptop-width layouts are unchanged.
export const ALERTS_TABLE_WIDTH_FRACTION = 0.45;

export const ALERTS_TABLE_OFFSET_STORAGE_KEY = "alertsTableWidthOffset";

export const getDefaultAlertsTableColumnWidth = (contentAreaWidth: number) =>
  Math.max(
    ALERTS_TABLE_COLUMN_BASE_WIDTH,
    Math.round(contentAreaWidth * ALERTS_TABLE_WIDTH_FRACTION)
  );

export const readStoredAlertsTableOffset = () => {
  try {
    const raw = window.localStorage.getItem(ALERTS_TABLE_OFFSET_STORAGE_KEY);
    if (raw === null) return 0;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? clampAlertsTableOffset(parsed) : 0;
  } catch {
    return 0;
  }
};

export const storeAlertsTableOffset = (offset: number) => {
  try {
    window.localStorage.setItem(
      ALERTS_TABLE_OFFSET_STORAGE_KEY,
      String(offset)
    );
  } catch {
    // localStorage unavailable (private mode) — width just won't persist
  }
};

export const clampAlertsTableOffset = (offset: number) =>
  Math.min(
    ALERTS_TABLE_MAX_OFFSET,
    Math.max(ALERTS_TABLE_MIN_OFFSET, Math.round(offset))
  );

export const getAlertsTableColumnWidth = (offset: number) =>
  ALERTS_TABLE_COLUMN_BASE_WIDTH + offset;

export const getAlertsTableInnerWidth = (offset: number) =>
  ALERTS_TABLE_INNER_BASE_WIDTH + offset;

interface AlertsTableColumnResizeHandleProps {
  offset: number;
  onOffsetChange: (offset: number) => void;
}

export const AlertsTableColumnResizeHandle: React.FC<
  AlertsTableColumnResizeHandleProps
> = ({ offset, onOffsetChange }) => {
  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startOffset = offset;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      onOffsetChange(
        clampAlertsTableOffset(startOffset + (moveEvent.clientX - startX))
      );
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <ResizeHandleButton
      ariaLabel="Resize alerts table"
      cursorClass="cursor-ew-resize"
      iconSx={{ transform: "rotate(45deg)" }}
      onMouseDown={handleMouseDown}
    />
  );
};
